import React, { useCallback, useRef, useMemo, useContext, startTransition } from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import NotificationIcon from '@wix/da-ds/pkg/Icons/24x24/Notification';
import { BiData } from '@wix/da-bi/pkg/events';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { useNotificationDropdownHover } from '../../hooks/useNotificationDropdownHover';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './NotificationsDropdownWrapper.scss';
const NotificationsDropdownWrapper = ({ feedbackCount, className, iconClassName, numberClassName, openNotificationsDropdown, closeNotificationsDropdown, isLegacyHeader, isFeedbackPageOpen, isNotificationsDropdownOpen, toggleFeedbackPage, toggleBurgerMenu, 'aria-label': ariaLabel })=>{
    const { t } = useTranslation();
    const { DrawerComponent, isStandalonePage } = useContext(NCContext);
    const { handleNotificationsDropdownHover, handleNotificationsDropdownLeave } = useNotificationDropdownHover({
        isNotificationsDropdownOpen,
        openNotificationsDropdown,
        closeNotificationsDropdown
    });
    // no point to spamming this if our guest has twitchy hands
    const throttledMouseLeave = useMemo(()=>throttle(handleNotificationsDropdownLeave, 300), [
        handleNotificationsDropdownLeave
    ]);
    const rootRef = useRef(null);
    const onClose = useCallback(()=>{
        throttledMouseLeave();
    }, [
        throttledMouseLeave
    ]);
    const handleClickOutside = useCallback(()=>{
        if (!isNotificationsDropdownOpen) {
            return;
        }
        onClose();
    }, [
        isNotificationsDropdownOpen,
        onClose
    ]);
    useOnOutsideClick(rootRef, handleClickOutside);
    const handleIconClick = useCallback((e)=>{
        if (isLegacyHeader) {
            window.location.assign(Url.feedbackLink());
            return;
        }
        if (!isStandalonePage) {
            // We can use startTransition outright. useTransition's pending state is unnecessary since
            // a loading screen gets mounted immediately.
            startTransition(()=>{
                yieldToMain(()=>{
                    toggleBurgerMenu(false); // Close network bar, if it's open
                    toggleFeedbackPage();
                });
            });
        }
        // We put an `href` attribute on the element that calls this so users can open notifications in a new tab,
        // so we prevent default to avoid the standard handling of an anchor tag click since we're duper browsing.
        e.preventDefault();
    }, [
        isStandalonePage,
        isLegacyHeader,
        toggleFeedbackPage,
        toggleBurgerMenu
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        onMouseEnter: ()=>{
            if (!isFeedbackPageOpen && !isStandalonePage) {
                handleNotificationsDropdownHover();
            }
        },
        onMouseLeave: ()=>handleNotificationsDropdownLeave(),
        ref: rootRef
    }, /*#__PURE__*/ React.createElement(IconButtonWithNotification, {
        icon: NotificationIcon,
        size: "medium",
        notificationColor: "red",
        iconButtonClassName: iconClassName,
        numberClassName: numberClassName,
        count: feedbackCount,
        biData: BiData({
            evid: 700,
            link_name: 'notifications',
            indicator: feedbackCount
        }),
        href: isStandalonePage || isFeedbackPageOpen ? undefined : Url.feedbackLink(),
        onClick: handleIconClick,
        "aria-label": t('siteHeader.a11y.cart.withItems', {
            formattedCount: feedbackCount
        })
    }), DrawerComponent && !isStandalonePage && /*#__PURE__*/ React.createElement("div", {
        role: "dialog",
        id: "site-header-notifications",
        className: s['notifications-dropdown-wrapper']
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['container'], isNotificationsDropdownOpen && s['active'])
    }, isNotificationsDropdownOpen && /*#__PURE__*/ React.createElement(DrawerComponent, {
        isDropdown: true,
        openFeedbackPage: ()=>toggleFeedbackPage && toggleFeedbackPage()
    }))));
};
NotificationsDropdownWrapper.displayName = 'NotificationsDropdownWrapper';
export default NotificationsDropdownWrapper;

//# sourceMappingURL=NotificationsDropdownWrapper.js.map